import {
  Component,
  h,
  Prop,
  Event,
  EventEmitter,
  State,
  Watch,
} from '@stencil/core';

@Component({
  tag: 'signet-pagination',
  styleUrl: 'signet-pagination.scss',
  shadow: true,
})
export class SignetPagination {
  @Prop() currentPage = 1;
  @Prop() totalPages = 1;
  @Prop() pageSize = 10;
  @Prop() totalItems = 1;
  @Prop() pageSizeOptions: number[] | string;
  @Event() pageChange: EventEmitter<number>;
  @Event() pageSizeChange: EventEmitter<number>;

  @State() goToPage: number = this.currentPage;
  @State() private _pageSizeOptions: number[];
  @State() _totalPages = this.totalPages;

  @Watch('pageSizeOptions')
  parsePageSizeOptionsProp(newValue: number[] | string) {
    if (typeof newValue === 'string') {
      this._pageSizeOptions = JSON.parse(newValue);
    } else {
      this._pageSizeOptions = newValue;
    }
  }

  componentWillLoad() {
    this.parsePageSizeOptionsProp(this.pageSizeOptions);
  }

  handleClick(pageNumber: number) {
    if (
      pageNumber !== this.currentPage &&
      pageNumber >= 1 &&
      pageNumber <= this.totalPages
    ) {
      this.currentPage = pageNumber;
      this.goToPage = pageNumber;
      this.pageChange.emit(pageNumber);
    }
  }

  handlePrevious() {
    if (this.currentPage > 1) {
      this.currentPage = this.currentPage - 1;
      this.goToPage = this.goToPage - 1;
      this.pageChange.emit(this.currentPage);
    }
  }

  handleNext() {
    if (this.currentPage < this.totalPages) {
      this.currentPage = this.currentPage + 1;
      this.goToPage = this.goToPage + 1;
      this.pageChange.emit(this.currentPage);
    }
  }

  handlePageSizeChange(event: Event) {
    const select = event.target as HTMLSelectElement;
    this.pageSize = parseInt(select.value, 10);
    this.totalPages = this.pageSize;
    this.pageSizeChange.emit(this.pageSize);
  }

  handleGoToPageChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.goToPage = parseInt(input.value, 10);
  }

  handleGoToPageSubmit(event: Event) {
    event.preventDefault();
    if (this.goToPage >= 1 && this.goToPage <= this.totalPages) {
      this.currentPage = this.goToPage;
      this.pageChange.emit(this.goToPage);
    }
  }

  render() {
    const maxVisiblePages = 5;
    const pages = [];
    const ellipsis = <span class="pagination-ellipsis">...</span>;

    let startPage = 1;
    let endPage = Math.min(startPage + maxVisiblePages - 1, this.totalPages);

    if (this.currentPage > Math.floor(maxVisiblePages / 2)) {
      startPage = this.currentPage - Math.floor(maxVisiblePages / 2);
      endPage = Math.min(
        this.currentPage + Math.ceil(maxVisiblePages / 2) - 1,
        this.totalPages
      );
    }

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(endPage - maxVisiblePages + 1, 1);
    }

    pages.push(
      <button
        class="pagination-previous"
        onClick={() => this.handlePrevious()}
        disabled={this.currentPage === 1}
      >
        <signet-icon icon="keyboard_arrow_left"></signet-icon>
      </button>
    );

    if (startPage > 1) {
      pages.splice(1, 0, ellipsis);
    }

    for (let i = startPage; i <= endPage; i++) {
      const isCurrentPage = i === this.currentPage;
      pages.push(
        <button
          class={`pagination-page ${isCurrentPage ? 'active' : ''}`}
          onClick={() => this.handleClick(i)}
        >
          {i}
        </button>
      );
    }

    pages.push(
      <button
        class="pagination-next"
        onClick={() => this.handleNext()}
        disabled={this.currentPage === this.totalPages}
      >
        <signet-icon icon="keyboard_arrow_right"></signet-icon>
      </button>
    );

    return (
      <div class="pagination-container">
        <div class="pagination-info">Total {this.totalItems}</div>
        <div class="pagination-size">
          <label htmlFor="page-size">Page sizes: </label>
          <select
            id="page-size"
            name="page-size"
            onChange={(event) => this.handlePageSizeChange(event)}
          >
            {this._pageSizeOptions.map((size) => (
              <option value={size} selected={this.totalPages === size}>
                {size}/page
              </option>
            ))}
          </select>
        </div>
        {pages}
        <form
          class="pagination-go-to"
          onSubmit={(event) => this.handleGoToPageSubmit(event)}
        >
          <label htmlFor="go-to">Go to</label>
          <input
            id="go-to"
            name="go-to"
            type="number"
            value={this.currentPage}
            onInput={(event) => this.handleGoToPageChange(event)}
          />
        </form>
      </div>
    );
  }
}
